// src/services/faqService.js
import api from '../utils/api'; // Your API instance

const FAQ_BASE_URL = '/faqs';

const faqService = {
  // Get all FAQs with pagination, filtering, and search
  getFaqs: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      // Add pagination
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      
      // Add search
      if (params.search) queryParams.append('search', params.search);
      
      // Add filters
      if (params.status) queryParams.append('status', params.status);
      if (params.categoryId) queryParams.append('categoryId', params.categoryId);
      if (params.tagId) queryParams.append('tagId', params.tagId);
      if (params.authorId) queryParams.append('authorId', params.authorId);
      if (params.helpful !== undefined) queryParams.append('helpful', params.helpful);
      
      // Add sorting
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

      const response = await api.get(`${FAQ_BASE_URL}?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get single FAQ by ID
  getFaqById: async (id) => {
    try {
      const response = await api.get(`${FAQ_BASE_URL}/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get FAQ by slug
  getFaqBySlug: async (slug) => {
    try {
      const response = await api.get(`${FAQ_BASE_URL}/slug/${slug}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Create new FAQ
  createFaq: async (faqData) => {
    try {
      const response = await api.post(FAQ_BASE_URL, faqData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update FAQ
  updateFaq: async (id, faqData) => {
    try {
      const response = await api.put(`${FAQ_BASE_URL}/${id}`, faqData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Partial update FAQ
  patchFaq: async (id, faqData) => {
    try {
      const response = await api.patch(`${FAQ_BASE_URL}/${id}`, faqData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Delete FAQ
  deleteFaq: async (id) => {
    try {
      const response = await api.delete(`${FAQ_BASE_URL}/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  deleteDocument: async (faqId, documentId) => {
    try {
      const response = await api.delete(`${FAQ_BASE_URL}/${faqId}/document/${documentId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update FAQ status
  updateFaqStatus: async (id, status) => {
    try {
      const response = await api.patch(`${FAQ_BASE_URL}/${id}/status`, { status });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get FAQ feedback
  getFaqFeedback: async (faqId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.rating) queryParams.append('rating', params.rating);
      
      const response = await api.get(
        `${FAQ_BASE_URL}/${faqId}/feedback?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get related FAQs
  getRelatedFaqs: async (faqId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.append('limit', params.limit);
      
      const response = await api.get(
        `${FAQ_BASE_URL}/${faqId}/related?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Add related FAQ
  addRelatedFaq: async (faqId, relatedFaqId, relationStrength = 1) => {
    try {
      const response = await api.post(`${FAQ_BASE_URL}/${faqId}/related`, {
        relatedFaqId,
        relationStrength,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Remove related FAQ
  removeRelatedFaq: async (faqId, relatedFaqId) => {
    try {
      const response = await api.delete(
        `${FAQ_BASE_URL}/${faqId}/related/${relatedFaqId}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Upload FAQ image
  uploadFaqImage: async (faqId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post(
        `${FAQ_BASE_URL}/${faqId}/images`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Delete FAQ image
  deleteFaqImage: async (faqId, imageId) => {
    try {
      const response = await api.delete(`${FAQ_BASE_URL}/${faqId}/images/${imageId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get FAQ categories
  getFaqCategories: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.search) queryParams.append('search', params.search);
      
      const response = await api.get(`/categories?type=FAQ&${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get FAQ tags
  getFaqTags: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.search) queryParams.append('search', params.search);
      
      const response = await api.get(`/tags?type=FAQ&${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Bulk delete FAQs
  bulkDeleteFaqs: async (ids) => {
    try {
      const response = await api.delete(`${FAQ_BASE_URL}/bulk`, { data: { ids } });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get FAQ statistics
  getFaqStats: async () => {
    try {
      const response = await api.get(`${FAQ_BASE_URL}/stats`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Record view
  recordView: async (faqId) => {
    try {
      const response = await api.post(`${FAQ_BASE_URL}/${faqId}/view`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Record feedback
  recordFeedback: async (faqId, feedbackData) => {
    try {
      const response = await api.post(`${FAQ_BASE_URL}/${faqId}/feedback`, feedbackData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};

export default faqService;